import PropTypes from 'prop-types';
import hash from 'object-hash';

const testUrls = [
  '/renault/5',
  '/honda/e',
  '/audi/q4-e-tron',
  '/toyota/rav4',
  '/byd/atto-3',
  '/ford/explorer',
  '/cupra/tavascan',
  '/tesla/model-y',
  '/nissan/qashqai',
  '/vauxhall/corsa',
  '/audi/q5',
  '/polestar/2',
  '/kia/niro-ev',
  '/bmw/ix',
  '/mg/mg3',
  '/mg/zs',
  '/jeep/avenger',
  '/bmw/ix1',
  '/volvo/ex30',
  '/byd/seal',
  '/skoda/enyaq',
  '/renault/austral',
  '/kia/niro',
  '/polestar/3',
  '/kia/sportage',
  '/mg/hs',
  '/hyundai/kona',
  '/audi/q6-e-tron',
  '/byd/seal-u',
  '/ford/ranger',
  '/peugeot/e-5008',
  '/volvo/xc40',
  '/vauxhall/corsa/electric',
  '/mini/aceman',
  '/bmw/x1',
  '/skoda/kamiq',
  '/ford/puma/st',
  '/cupra/terramar',
  '/toyota/aygo-x',
  '/mg/zs/ev',
  '/hyundai/tucson',
  '/mercedes/eqe',
  '/suzuki/swift',
  '/kia/ev3',
  '/alfa-romeo/junior',
  '/mercedes/gla',
  '/kia/stonic',
  '/genesis/gv70',
  '/dacia/jogger',
  '/byd/dolphin',
];

/**
 * Formats a number to a string with specified decimal places, decimal character, and thousands separator.
 * @param {Number} number - The number to format.
 * @param {Number} [decimals=0] - The number of decimal places to include.
 * @param {String} [decimalChar='.'] - The character to use for the decimal point.
 * @param {String} [separator=','] - The character to use as a thousands separator.
 * @return {String} - The formatted number as a string.
 */
const formatNumber = (number, decimals = 0, decimalChar = '.', separator = ',') => {
  if (number === null || number === undefined) {
    return '';
  }
  if (typeof number !== 'number' || Number.isNaN(number)) {
    throw new Error('Invalid number provided to formatNumber');
  }
  if (decimals < 0 || !Number.isInteger(decimals)) {
    throw new Error('Decimals must be a non-negative integer');
  }

  if (typeof decimalChar !== 'string' || typeof separator !== 'string') {
    throw new Error('Decimal character and separator must be strings');
  }

  if (decimalChar.length !== 1 || separator.length !== 1) {
    throw new Error('Decimal character and separator must be single characters');
  }

  return number
    .toFixed(decimals) // Convert to fixed decimals.
    .replace('.', decimalChar)
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

/**
 * Detects the variant based on the provided vehicle data.
 * @param {Object} data - The vehicle data.
 * @returns {String} - The detected variant.
 */
export const detectVariant = ({
  rrpMin, rrpMax, averageSaving, newCarDealsPrice, leasingDealsPrice, usedCarDealsPrice,
}) => {
  if (rrpMin && rrpMax && averageSaving && newCarDealsPrice && leasingDealsPrice && usedCarDealsPrice) return 'allData';
  if (rrpMin && rrpMax && averageSaving && newCarDealsPrice && leasingDealsPrice) return 'newAndLeasing';
  if (rrpMin && rrpMax && averageSaving && newCarDealsPrice) return 'newOnly';
  if (usedCarDealsPrice && leasingDealsPrice) return 'usedAndLeasing';
  if (usedCarDealsPrice) return 'usedOnly';
  return 'noData';
};

/**
 * Builds a formatted text paragraph for vehicle pricing information.
 * @param {String} make - The make of the vehicle.
 * @param {String} model - The model of the vehicle.
 * @param {Number} rrpMin - The minimum recommended retail price (RRP) of the vehicle.
 * @param {Number} rrpMax - The maximum recommended retail price (RRP) of the vehicle.
 * @param {Number} averageSaving - The average saving on the vehicle.
 * @param {Number} newCarDealsPrice - The starting price for a new vehicle.
 * @param {Number} leasingDealsPrice - The monthly price for leasing the vehicle.
 * @param {Number} usedCarDealsPrice - The starting price for a used vehicle.
 * @param {String} newCarDealsCtaLink - The call-to-action link for new car deals.
 * @param {String} leasingDealsCtaLink - The call-to-action link for leasing deals.
 * @param {String} usedCarDealsCtaLink - The link for used car deals.
 * @param {String} newCarDealsPricingLink - The link for new car deals pricing.
 * @returns {string|null}
 */
const generatePricingParagraph = ({
  make,
  model,
  rrpMin,
  rrpMax,
  averageSaving,
  newCarDealsPrice,
  leasingDealsPrice,
  usedCarDealsPrice,
  newCarDealsCtaLink,
  leasingDealsCtaLink,
  usedCarDealsCtaLink,
  newCarDealsPricingLink,
}) => {
  const variant = detectVariant({
    rrpMin, rrpMax, averageSaving, newCarDealsPrice, leasingDealsPrice, usedCarDealsPrice,
  });

  const newCarCtaLink = newCarDealsPricingLink || newCarDealsCtaLink;
  const className = '-trackingPlacement';
  const newCarDealsLink = newCarCtaLink ? `<a href="${newCarCtaLink}" class="${className}" data-tracking-placement="GYCPricingParagraphLinkNew" data-tracking-type="GYC">new car deals</a>` : '';
  const usedCarDealsLink = usedCarDealsCtaLink ? `<a href="${usedCarDealsCtaLink}" class="${className}" data-tracking-placement="GYCPricingParagraphLinkUsed" data-tracking-type="GYC">used car deals</a>` : '';
  const leasingDealsLink = leasingDealsCtaLink ? `<a href="${leasingDealsCtaLink}" class="${className}" data-tracking-placement="GYCPricingParagraphLinkLeasing" data-tracking-type="GYC">leasing deals</a>` : '';
  const sellMyCarLink = `<a href="/sell-my-car" class="${className}" data-tracking-placement="SMCPricingParagraphLinkSellMyCar" data-tracking-type="SMC">sell your car</a>`;

  const text1 = `How much does the ${make} ${model} cost? `
    + `Well, official ‘on the road’ prices range from £${formatNumber(rrpMin)} to £${formatNumber(rrpMax)} `
    + `but you can currently save an average of £${formatNumber(averageSaving)} through the Auto Express Find A Car `
    + `service, where prices start at £${formatNumber(newCarDealsPrice)}.`;

  const paragraph1 = {
    allData: `${text1} You can lease a ${make} ${model} from £${formatNumber(leasingDealsPrice)} per month or buy a used model at prices starting from £${formatNumber(usedCarDealsPrice)}.`,
    newAndLeasing: `${text1} Don’t want to buy? You can lease a ${make} ${model} from £${formatNumber(leasingDealsPrice)} per month.`,
    newOnly: text1,
    usedAndLeasing: `You can lease a ${make} ${model} from £${formatNumber(leasingDealsPrice)} per month or buy a used model at prices starting from £${formatNumber(usedCarDealsPrice)}.`,
    usedOnly: `You can buy a used ${make} ${model} right now on Auto Express. Prices starting from £${formatNumber(usedCarDealsPrice)}.`,
  };

  const paragraph2part1 = 'Check out our latest ';
  const paragraph2part2 = {
    allData: `${newCarDealsLink}, ${leasingDealsLink} and ${usedCarDealsLink} `,
    newAndLeasing: `${newCarDealsLink} and ${leasingDealsLink} `,
    newOnly: `${newCarDealsLink} `,
    usedAndLeasing: `${leasingDealsLink} and ${usedCarDealsLink} `,
    usedOnly: `${usedCarDealsLink} `,
  };

  const paragraph2part3 = `for the top offers available now on Auto Express. And don't forget we can help you ${sellMyCarLink}, too.`;

  const paragraph2 = paragraph2part1 + paragraph2part2[variant] + paragraph2part3;

  return paragraph1[variant] ? `<p>${paragraph1[variant]}</p><p>${paragraph2}</p>` : null;
};

/**
 * Counts the number of words in a given text.
 * @param {String} text
 */
export const count = text => text
  .replace(/<\/\w+>/ig, ' ')
  .replace(/(&nbsp;|<([^>]+)>|\n)/ig, '')
  .split(/\s+/)
  .filter(item => item).length;

/**
 * Injects a paragraph into the body after a specific header.
 * @param {Array} body - The body array containing content blocks.
 * @param {String} paragraph - The paragraph to inject.
 * @returns {Array} - The modified body with the injected paragraph.
 */
export const injectParagraph = (body, paragraph) => {
  if (!Array.isArray(body) || body.length === 0 || !paragraph) {
    return body;
  }

  const regex = /(\w+\s+\w+)\s+prices and latest deals/;

  const headerIndex = body.findIndex(item => item.type === 'HEADER' && regex.test(item.data));
  if (headerIndex === -1) {
    return body; // No matching header found, return original body
  }

  // Insert the paragraph after the header
  const modifiedBody = [...body];
  modifiedBody.splice(headerIndex + 1, 0, {
    __typename: 'DataBlock',
    type: 'TEXT',
    title: null,
    data: paragraph,
    id: hash({ time: new Date(), obj: paragraph }),
    wordCount: count(paragraph),
  });

  return modifiedBody;
};

/**
 * Generates a pricing paragraph based on vehicle data and URL.
 * @param {Object} vehicleData - The vehicle data object containing details about the vehicle.
 * @param {String} url - The URL to validate.
 * @returns {String|null} - The generated pricing paragraph or null.
 */
export const getPricingParagraph = ({ vehicleData, url }) => {
  if (!testUrls.includes(url)) return null;

  return generatePricingParagraph({
    make: vehicleData?.make,
    model: vehicleData?.model,
    averageSaving: vehicleData?.averageSaving,
    rrpMin: vehicleData?.rrpMin,
    rrpMax: vehicleData?.rrpMax,
    newCarDealsPrice: vehicleData?.newCarDealsPrice,
    leasingDealsPrice: vehicleData?.leasingDealsPrice,
    usedCarDealsPrice: vehicleData?.usedCarDealsPrice,
    newCarDealsCtaLink: vehicleData?.newCarDealsCtaLink,
    leasingDealsCtaLink: vehicleData?.leasingDealsCtaLink,
    usedCarDealsCtaLink: vehicleData?.usedCarDealsCtaLink,
    newCarDealsPricingLink: vehicleData?.newCarDealsPricingLink,
  });
};

getPricingParagraph.propTypes = {
  vehicleData: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    rrpMin: PropTypes.number,
    rrpMax: PropTypes.number,
    averageSaving: PropTypes.number,
    newCarDealsPrice: PropTypes.number,
    leasingDealsPrice: PropTypes.number,
    usedCarDealsPrice: PropTypes.number,
    newCarDealsCtaLink: PropTypes.string,
    leasingDealsCtaLink: PropTypes.string,
    usedCarDealsCtaLink: PropTypes.string,
    newCarDealsPricingLink: PropTypes.string,
  }),
  url: PropTypes.string,
};
